import { useEffect, useState, useRef } from 'react';
import parse from 'html-react-parser';
import { Modal } from 'react-bootstrap';

import { Parameter } from '../../classess/parameter/parameter';
import {
    getProcessButtons, isUndNullEmp, getLanguageObj, getResponse, getRolesType, handleResponseMessageAsync, settings, styles
} from '../../helpers/main';
import { Customer } from '../../classess/customer/customer';
import { GlobalFormats } from '../../classess/main/globalFormats';
import { ParameterService } from '../../services/parameters/ParameterService';

const { html, icons, images, labels, messages } = settings;

const currentMethod = 'ParameterAdd';

export default function ParameterAdd({
    auth = {},
    modalObj = {
        $: {},
        type: 0,
        auth: auth,
        showModal: false,
        languageObj: GlobalFormats?.getClass(getLanguageObj({ subMethod: currentMethod })?.result),
        dtParametersId: '',
        dtParametersRowId: 0,
        parameter: Parameter?.getClass({}, false),
        customers: [Customer?.getClass({}, false)],
        userRoles: getRolesType(),
    },
    setModalObj = {},
}) {
    //#region variables
    const isMount = useRef(true);
    const [process, setProcess] = useState({
        showLoading: false,
        save: false,
        back: false,
    });
    const [errors, setErrors] = useState({
        customer: '',
    });

    const controls = {
        buttons: {
            back: {
                defaultClass: 'btn btn-sm btn-outline-success',
                id: 'btnModalBack',
            },
            save: {
                defaultClass: 'btn btn-sm btn-outline-primary mx-1 mr-2',
                id: 'btnModalSave',
            },
            closeModal: {
                defaultClass: 'close btn-danger fas fa-window-close',
                id: 'btnModalClose',
            },
        },
        checkBoxs: {
            isActive: {
                id: 'chkModalIsActive',
                defaultClass: '',
            }
        },
        icons: {
            save: {
                id: 'iconModalSave',
                defaultClass: icons?.fasFa?.save,
            },
            back: {
                id: 'iconModalBack',
                defaultClass: icons?.fasFa?.arrowCircleLeft,
            },
        },
        inputs: {
        },
        pickers: {
        },
        selects: {
            customers: {
                id: 'selModalCustomer',
                defaultClass: 'custom-select text-black bg-transparent',
            },
        },
        spiners: {
            save: {
                id: 'spModalSave',
                defaultClass: '',
            }
        },
        textAreas: {
        },
    };
    //#endregion variables

    //#region methods
    //#region parameters
    const addParameter = async ({
        e = undefined,
        subMethod = '',
    } = {}) => {
        let response = getResponse({ type: 0 });
        const currentMethod = 'addParameter';
        try {
            e?.preventDefault?.();

            response = setErrorsDefault({ subMethod: currentMethod });
            if (!response?.isSuccess || response?.isWarning) return;

            response = validateFormData({ subMethod: currentMethod });
            if (!response?.isSuccess || response?.isWarning) return;

            response = setData({ subMethod: currentMethod });
            if (!response?.isSuccess || response?.isWarning) return;
            const setDataParameter = response?.result?.parameter || modalObj?.parameter;

            setProcess(prev => ({ ...prev, save: true, showLoading: true }));

            response = await ParameterService.addAsync({
                auth: modalObj?.auth,
                parameter: setDataParameter,
                languageId: modalObj?.languageObj?.id,
                subMethod: currentMethod,
            });
            if (!response?.isSuccess || response?.isWarning) return;

            //#region datatable add
            const modelParameter = Parameter?.getClass(response?.result?.parameter || setDataParameter, false);
            const modelCustomer = modalObj?.customers?.find(({ id }) => id === modelParameter?.customerId) || {};

            modalObj?.$(`#${modalObj?.dtParametersId}`).DataTable().row.add([
                modelCustomer?.name || '',
                0, // initial details count
                getProcessButtons({
                    id: modelParameter?.id,
                    description: modelCustomer?.name,
                    showDetails: true,
                    labelDetails: labels?.details[modalObj?.languageObj?.id],
                    enabledDetails: true,
                    showDelete: (modalObj?.userRoles?.isAdmin || modalObj?.userRoles?.isAnalyst),
                    labelDelete: labels?.delete[modalObj?.languageObj?.id],
                    enabledDelete: (modalObj?.userRoles?.isAdmin || modalObj?.userRoles?.isAnalyst),
                    showEdit: (modalObj?.userRoles?.isAdmin || modalObj?.userRoles?.isAnalyst),
                    labelEdit: labels?.edit?.[modalObj?.languageObj?.id],
                    enabledEdit: (modalObj?.userRoles?.isAdmin || modalObj?.userRoles?.isAnalyst),
                })?.result,
            ])?.draw(true);
            //#endregion datatable add

            response = getResponse({
                type: 0,
                msgText: `${messages?.processCompletedSuccessfully?.[modalObj?.languageObj?.id]}.`,
            });
        } catch (ex) {
            response = getResponse({ type: -1 });
            response.msgText = `Method: ${currentMethod}<br/>`;
            if (!isUndNullEmp({ data: subMethod, subMethod: currentMethod })?.result) {
                response.msgText += `Sub method: ${subMethod}<br/>`;
            }
            response.msgText += `Error: ${ex?.message}<br/>`;
        } finally {
            if (!isMount?.current) return;
            setProcess(prev => ({ ...prev, save: false, showLoading: false }));

            if (response?.isSuccess && !response?.isWarning) {
                setDefaultValue({ closeModal: true, subMethod: currentMethod });
            }

            // CSN:
            if (!response?.isSuccess || response?.isWarning || response?.msgText !== '' || response?.msgTextHtml !== '') {
                await handleResponseMessageAsync({
                    response,
                    currentMethod,
                    auth: modalObj?.auth,
                    languageId: modalObj?.languageObj?.id
                });
            }
        }
    }
    //#endregion parameters

    //#region others
    const getData = async ({
        subMethod = '',
    } = {}) => {
        let response = getResponse({ type: 0 });
        const currentMethod = 'getData';
        try {
            setProcess(prev => ({ ...prev, showLoading: true }));
            // No asyc calls needed for Add, only initializing if needed.
        } catch (ex) {
            response = getResponse({ type: -1 });
            response.msgText = `Method: ${currentMethod}<br/>`;
            if (!isUndNullEmp({ data: subMethod, subMethod: currentMethod })?.result) {
                response.msgText += `Sub method: ${subMethod}<br/>`;
            }
            response.msgText += `Error: ${ex?.message}<br/>`;
        } finally {
            if (!isMount?.current) return;
            setProcess(prev => ({ ...prev, showLoading: false }));

            // CSN:
            if (!response?.isSuccess || response?.isWarning || response?.msgText !== '' || response?.msgTextHtml !== '') {
                await handleResponseMessageAsync({
                    response, currentMethod,
                    auth: modalObj?.auth,
                    languageId: modalObj?.languageObj?.id
                });
            }
        }
    }

    const setDefaultValue = ({
        closeModal = false,
        subMethod = '',
    } = {}) => {
        const currentMethod = 'setDefaultValue';
        try {
            if (closeModal) {
                setModalObj(prev => ({
                    ...prev,
                    showModal: false,
                    type: 0,
                    parameter: Parameter?.getClass({}, false),
                }));
            }
        } catch (ex) {
            let response = getResponse({ type: -1 });
            response.msgText = `Method: ${currentMethod}<br/>`;
            if (!isUndNullEmp({ data: subMethod, subMethod: currentMethod })?.result) {
                response.msgText += `Sub method: ${subMethod}<br/>`;
            }
            response.msgText += `Error: ${ex?.message}<br/>`;
            console?.error(response?.msgText);
        }
    }

    const setData = ({
        subMethod = '',
    } = {}) => {
        const currentMethod = 'setData';
        let response = getResponse({ type: 0 });
        try {
            const setDataParameter = {
                ...modalObj?.parameter,
                // Add any extra data processing if needed
            };

            setModalObj(prev => ({ ...prev, parameter: setDataParameter }));
            response.result = { parameter: setDataParameter };
        } catch (ex) {
            response = getResponse({ type: -1 });
            response.msgText = `Method: ${currentMethod}<br/>`;
            if (!isUndNullEmp({ data: subMethod, subMethod: currentMethod })?.result) {
                response.msgText += `Sub method: ${subMethod}<br/>`;
            }
            response.msgText += `Error: ${ex?.message}<br/>`;
        }
        return response;
    }

    const validateFormData = ({
        subMethod = '',
    } = {}) => {
        const currentMethod = 'validateFormData';
        let response = getResponse({ type: 0 });
        try {
            if (isUndNullEmp({ data: modalObj?.parameter?.customerId, subMethod: currentMethod })?.result ||
                parseInt(modalObj?.parameter?.customerId) <= 0) {
                setErrors(prev => ({ ...prev, customer: `${messages?.thisFieldRequired?.[modalObj?.languageObj?.id]}.` }));
                response = getResponse({ type: 1 });
            }
        } catch (ex) {
            response = getResponse({ type: -1 });
            response.msgText = `Method: ${currentMethod}<br/>`;
            if (!isUndNullEmp({ data: subMethod, subMethod: currentMethod })?.result) {
                response.msgText += `Sub method: ${subMethod}<br/>`;
            }
            response.msgText += `Error: ${ex?.message}<br/>`;
        }
        return response;
    }

    const setErrorsDefault = ({
        subMethod = '',
    } = {}) => {
        let response = getResponse({ type: 0 });
        const currentMethod = 'setErrorsDefault';
        try {
            setErrors(prev => ({ ...prev, customer: '' }));
        } catch (ex) {
            response = getResponse({ type: -1 });
            response.msgText = `Method: ${currentMethod}<br/>`;
            if (!isUndNullEmp({ data: subMethod, subMethod: currentMethod })?.result) {
                response.msgText += `Sub method: ${subMethod}<br/>`;
            }
            response.msgText += `Error: ${ex?.message}<br/>`;
        }
        return response;
    }

    useEffect(() => {
        isMount.current = true;
        getData({ subMethod: 'form - onLoad' });
        return () => { isMount.current = false; };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    //#endregion others
    //#endregion methods

    return (
        <Modal backdrop='static' show={modalObj?.showModal} centered>
            <Modal.Header className='border-bottom-0 pt-4 px-4 bg-white'>
                <Modal.Title className='d-flex align-items-center w-100'>
                    <div className='bg-light p-2 rounded-circle mr-3 shadow-sm'
                        style={{ width: '45px', height: '45px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <img alt='parameters' style={{ ...styles?.cardHeaderLogo }} src={images?.icons?.parameters} />
                    </div>
                    <div className='d-flex flex-column'>
                        <span className='h5 mb-0 font-weight-bold text-dark'>{labels?.parameter?.[modalObj?.languageObj?.id]}</span>
                        <div className='d-flex align-items-center mt-1'>
                            <span className={`badge badge-pill py-1 px-2 text-uppercase ${modalObj?.type === 0 ? 'badge-info' : modalObj?.type === 1 ? 'badge-warning text-dark' : 'badge-secondary'}`} style={html?.badges?.micro}>
                                {modalObj?.type === 0 ? labels?.add?.[modalObj?.languageObj?.id] :
                                    modalObj?.type === 1 ? labels?.edit?.[modalObj?.languageObj?.id] :
                                        labels?.detail?.[modalObj?.languageObj?.id]}
                            </span>
                        </div>
                    </div>
                </Modal.Title>
                <button id={controls?.buttons?.closeModal?.id}
                    className='close border-0 bg-transparent'
                    disabled={process?.showLoading}
                    style={{ fontSize: '1.5rem', marginTop: '-15px' }}
                    onClick={() => setDefaultValue({ closeModal: true, subMethod: 'btnClose' })}>
                    <span aria-hidden='true'>&times;</span>
                </button>
            </Modal.Header>
            <form onSubmit={(e) => addParameter({ e: e, subMethod: 'form - onSubmit' })}>
                <Modal.Body className='p-0'>
                    <div className='card-body p-4'>
                        <div className='row'>
                            <div className='col-12'>
                                <div className='form-group'>
                                    <small className={settings?.html?.labels?.mutedUpperFontWeightBoldDblockMb2}>
                                        <i className={icons?.fasFa?.building} /> {labels?.customer?.[modalObj?.languageObj?.id]}
                                    </small>
                                    <select id={controls?.selects.customers.id}
                                        className='custom-select bg-light border-0 shadow-sm text-dark'
                                        style={{ borderRadius: '5px' }}
                                        disabled={process?.showLoading || modalObj?.type === 2}
                                        value={!isUndNullEmp({ data: modalObj?.parameter?.customerId, subMethod: controls?.selects.customers.id, })?.result ?
                                            modalObj?.parameter?.customerId : `0`
                                        }
                                        onChange={(e) => setModalObj(prev => ({
                                            ...prev,
                                            parameter: { ...prev?.parameter, customerId: parseInt(e?.target?.value) },
                                        }))}
                                        onFocus={() => setErrors(prev => ({ ...prev, customer: '' }))}>
                                        <option key={`nothing`} value={`0`}>{messages?.selectCustomer?.[modalObj?.languageObj?.id]}</option>
                                        {modalObj?.customers?.map((customer) => (
                                            <option key={customer.id} value={customer.id}>{customer.name}</option>
                                        ))}
                                    </select>
                                    <div className='form-text' style={styles?.inputMsgAlert}>{parse(errors?.customer)}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer className='border-top-0 pb-4 px-4 bg-white'>
                    <div className='d-flex justify-content-center w-100'>
                        <button id={controls?.buttons?.save?.id}
                            className='btn btn-primary shadow-sm font-weight-bold mx-1 px-4'
                            style={{ borderRadius: '5px' }}
                            disabled={process?.showLoading}
                            hidden={modalObj?.type === 2}
                            type='submit'>
                            {process?.save ? (
                                <span className={settings?.react?.html?.spinner?.borderSm}
                                    role='status' aria-hidden='true'></span>
                            ) : (
                                <i id={controls?.icons?.save?.id} className={controls?.icons?.save?.defaultClass} />
                            )} {modalObj?.type === 0 ? labels?.add?.[modalObj?.languageObj?.id] : labels?.save?.[modalObj?.languageObj?.id]}
                        </button>
                        <button id={controls?.buttons?.back?.id}
                            className='btn btn-outline-secondary shadow-sm font-weight-bold mx-1 px-4'
                            style={{ borderRadius: '5px' }}
                            disabled={process?.showLoading}
                            type='button'
                            onClick={() => setDefaultValue({ closeModal: true, subMethod: 'btnBack' })}>
                            {process?.back ? (
                                <span className={settings?.react?.html?.spinner?.borderSm}
                                    role='status' aria-hidden='true'></span>
                            ) : (
                                <i id={controls?.icons?.back?.id} className={controls?.icons?.back?.defaultClass}></i>
                            )} {labels?.back?.[modalObj?.languageObj?.id]}
                        </button>
                    </div>
                </Modal.Footer>
            </form>
        </Modal>
    )
}